import {Directive, HostListener} from '@angular/core';

/**
 * Keeps a number field numeric while it is being typed in.
 *
 * The spec leaves the browser to decide what to do when text reaches `<input type="number">`:
 * Chrome and Safari drop the keystroke, Firefox accepts it and shows it. Either way the value
 * sanitizes to the empty string, which Angular's `NumberValueAccessor` turns into `null` — so in
 * Firefox the user reads "132sdf" in the field while the model holds nothing, and a form with no
 * `required` on it saves that as a cleared value. A validator cannot see this: by the time it runs
 * the control is indistinguishable from an empty one.
 *
 * So the character is refused instead, and every browser behaves the way Chrome already did.
 * Whether the number that survives is whole, positive or in range stays with the validators.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector -- matches the native element on purpose: every number field needs this, and one that forgot to opt in would keep the silent behaviour
  selector: 'input[type=number]',
})
export class NumberInputDirective {
  /**
   * A number as the field will accept it: digits, one sign, one separator, an exponent. The comma
   * is allowed because that is the decimal key on a Hungarian keyboard; the field cannot parse one,
   * so a value typed with it is cleared when the field is left, the same as any other unfinished
   * number.
   */
  private static readonly NUMERIC = /^-?\d*(?:[.,]\d*)?(?:e-?\d*)?$/i;

  /**
   * `beforeinput` rather than `keypress`: it covers pasting and dropping too, and it carries the
   * text being added, so a `-` or a separator is judged against what the field already holds
   * rather than by being a digit or not.
   *
   * The caret is not consulted: a number field supports no selection API, so `selectionStart` is
   * null there. The new text is appended to the current value instead, which decides every
   * character correctly and is only approximate about *where* a second separator or sign would
   * have landed — and a value with two of either is refused wherever they sit.
   */
  @HostListener('beforeinput', ['$event'])
  protected onBeforeInput(event: InputEvent): void {
    if (event.data == null) {
      // A deletion, or an input method mid-composition: nothing to vet.
      return;
    }
    const current = (event.target as HTMLInputElement).value;
    if (!NumberInputDirective.NUMERIC.test(current + event.data)) {
      event.preventDefault();
    }
  }

  /**
   * A lone `-` or `.` has to be typeable — they start `-5` and `.5` — but on their own they are not
   * a number, and the field reports its value as empty while still showing them. Leaving the field
   * settles it: what cannot be parsed is cleared, so what is on screen is what the form holds.
   */
  @HostListener('blur', ['$event.target'])
  protected onBlur(input: HTMLInputElement): void {
    if (!input.validity?.badInput) {
      return;
    }
    input.value = '';
    // The value was already `null` to Angular; this is what makes the control notice the field is
    // now empty in earnest, so it marks itself dirty and any `required` on it applies.
    input.dispatchEvent(new Event('input', {bubbles: true}));
  }
}
